import React, { useMemo } from 'react';
import { BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, fmtDate, useAdminGet } from '../kit';
import { Pill, num, rowsOf, str } from '../ipintel/shared';
import { AdminLink } from '../adminNav';
import { ConfidencePill, HorizonPill, LeverChips, clip, normCurrent } from '../horizon/shared';
import {
  CTA_LABELS, DailyBars, OpenLink, type Range, S, SECTION_LABELS, SHARE_LABELS, SOURCE_LABELS, STATES,
  STATE_LABELS, breakdown, eventLabel, fmtInt, fmtSeconds, nameOf, propsText, rangeParams, useEscape,
} from './shared';

/**
 * One page's story: GET tech_pages/page_detail {kind, target, from, to} —
 * traffic by day and by state, where visitors came from, how far into the
 * page they got (per-section views, clicks and locked clicks), which CTAs and
 * share channels they used, the latest events and sessions, every URL the
 * page has had (old ones 301 to the current one), and the preview links that
 * open the page as each state would see it (GET tech_pages/preview_links).
 * A tech page also shows its current Horizon data (page_detail.horizon — the
 * page's Problem & applications and Value levers sections), linked to the
 * Horizon data admin.
 */

export type PageRef = { kind: string; target: string; path?: string; title?: string; row?: any };

const EVENT_COLS = ['at', 'action', 'props', 'state', 'country', 'session_id'];
const EVENT_LABELS: Record<string, string> = {
  at: 'When', action: 'Event', props: 'Details', state: 'State', country: 'Country', session_id: 'Session',
};
const SESSION_COLS = ['at', 'user_email', 'country', 'referrer', 'events', 'session_id'];
const SESSION_LABELS: Record<string, string> = {
  at: 'Started', user_email: 'Account', country: 'Where', referrer: 'Came from', events: 'Events', session_id: 'Session',
};
const SECTION_COLS = ['section', 'views', 'clicks', 'locked_clicks', 'reach'];
const SECTION_COL_LABELS: Record<string, string> = {
  section: 'Section', views: 'Seen', clicks: 'Clicks', locked_clicks: 'Locked clicks', reach: 'Reach',
};

/** preview_links answers {pre_login: url, …}, {links: [{state, url}]} or [{state, url}]. */
const previewMap = (d: any): Record<string, string> => {
  const out: Record<string, string> = {};
  const arr = Array.isArray(d) ? d : Array.isArray(d?.links) ? d.links : null;
  if (arr) arr.forEach((x: any) => { if (x?.state && (x?.url || x?.path)) out[str(x.state)] = str(x.url || x.path); });
  else if (d && typeof d === 'object') {
    const src = d.urls && typeof d.urls === 'object' ? d.urls : d;
    STATES.forEach(s => { if (typeof src[s] === 'string') out[s] = src[s]; });
  }
  return out;
};

const withPreview = (path: string, state: string) =>
  path ? `${path}${path.includes('?') ? '&' : '?'}preview_state=${state}` : '';

const PageDrawer: React.FC<{ page: PageRef; range: Range; onClose: () => void }> = ({ page, range, onClose }) => {
  useEscape(onClose);
  const rp = rangeParams(range);
  const key = JSON.stringify([page.kind, page.target, rp]);
  const res = useAdminGet(S, 'page_detail', { kind: page.kind, target: page.target, ...rp }, [key]);
  const prev = useAdminGet(S, 'preview_links', { kind: page.kind, target: page.target }, [page.kind, page.target]);
  const d = res.data || {};
  const info = d.page || {};
  const path = str(info.path || page.path);
  const title = str(info.title || page.title) || page.target;
  const row = page.row || {};
  // The KPIs are page_detail's own totals (this page, this window, no list
  // filters). The list row — which may be filtered, or from Overview's top
  // pages with only views/visitors — only stands in until they arrive.
  const tot: any = d.totals && typeof d.totals === 'object' ? d.totals : null;
  const kpi = (k: string) => (tot ? tot[k] : row[k]);

  const byDay = rowsOf(d.by_day, 'days');
  const views = tot ? num(tot.views)
    : byDay.length ? byDay.reduce((a: number, x: any) => a + num(x?.views), 0) : num(row.views);
  const crawlerDays = rowsOf(d.crawler_hits_by_day, 'days');
  const crawler = crawlerDays.length
    ? crawlerDays.reduce((a: number, x: any) => a + num(x?.n ?? x?.hits ?? x?.crawler_hits), 0) : num(row.crawler_hits);
  const crawlerSeries = crawlerDays.map((x: any) => ({ day: x?.day ?? x?.date, hits: num(x?.n ?? x?.hits ?? x?.crawler_hits) }));

  const sections = useMemo(() => rowsOf(d.sections).map((s: any) => ({
    ...s, section: nameOf(s.section, SECTION_LABELS),
    reach: views ? `${Math.round((num(s.views) / views) * 100)}%` : '—',
  })), [d.sections, views]);
  const events = rowsOf(d.events);
  const sessions = rowsOf(d.sessions);
  const history: any[] = Array.isArray(info.paths_history) ? info.paths_history : [];
  const share = breakdown(d.share ?? row.share, k => nameOf(k, SHARE_LABELS));
  const links = previewMap(prev.data);
  const isTech = page.kind !== 'university';
  // page_detail.horizon: current_for() or null (pending); undefined = an older server that does not send it.
  const hzKnown = d.horizon !== undefined || (row.horizon && typeof row.horizon === 'object');
  const hz = normCurrent(d.horizon);
  const hzPending = !hz && (d.horizon === null || str(row.horizon?.status) === 'pending');

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative h-full bg-grayscale-100 shadow-xl flex flex-col" style={{ width: 'min(96vw, 60rem)' }}
        onClick={e => e.stopPropagation()} role="dialog" aria-label={`Page detail: ${title}`}>
        <div className="px-5 py-4 border-b border-grayscale-300 bg-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Pill tone="info">{page.kind === 'university' ? 'University page' : 'Tech page'}</Pill>
              <span className="text-[11px] font-mono text-grayscale-1100">{page.target}</span>
            </div>
            <div className="text-base font-bold text-grayscale-1300 font-grotesk break-words">{title}</div>
            <div className="text-xs text-grayscale-1100 break-all flex items-center gap-2 flex-wrap">
              <span className="font-mono">{path || 'no path yet'}</span>
              {path && <OpenLink path={path} label="Open page ↗" />}
            </div>
          </div>
          <Btn small onClick={onClose}>Close</Btn>
        </div>

        <div className="p-5 overflow-y-auto flex flex-col gap-4">
          {res.error && <Msg kind="error">{res.error}</Msg>}
          {res.loading && !res.data ? <Loading label="Reading this page's traffic…" /> : (
            <>
              <KPIRow>
                <KPI label="Views" value={fmtInt(views)} hint={`${range.from} → ${range.to}`} />
                <KPI label="Visitors" value={fmtInt(kpi('visitors') ?? 0)} />
                {kpi('avg_seconds') !== undefined && (
                  <KPI label="Avg. time" value={fmtSeconds(kpi('avg_seconds'))} />
                )}
                <KPI label="Sign-ups" value={fmtInt(kpi('signups') ?? 0)} hint="started on this page" />
                <KPI label="Crawler hits" value={fmtInt(crawler)} hint="server-side, bots only" />
              </KPIRow>

              {isTech && hzKnown && (
                <Section title="Horizon data on this page"
                  right={<AdminLink to={{ group: 'horizon', tab: 'technologies', tech: page.target }}
                    className="text-xs text-primary-700 underline underline-offset-2 whitespace-nowrap"
                    title="Current data, every result, the prompt and run it came from">Open in Horizon data →</AdminLink>}>
                  {hz ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1200">
                        <HorizonPill h={hz.horizon} long />
                        <ConfidencePill c={hz.confidence} />
                        {hz.industry && <span>{hz.industry}</span>}
                        <span className="text-grayscale-1100">· {hz.applications.length} application{hz.applications.length === 1 ? '' : 's'}</span>
                      </div>
                      {hz.problem && (
                        <p className="text-sm text-grayscale-1300 break-words" title={hz.problem}>
                          <span className="text-xs font-bold text-grayscale-1200">Problem: </span>{clip(hz.problem, 280)}
                        </p>
                      )}
                      <LeverChips levers={hz.levers} max={8} />
                    </>
                  ) : hzPending ? (
                    <div className="text-sm text-amber-700">
                      Pending — no Horizon result for this technology yet, so the page shows placeholders for Problem
                      &amp; applications and Value levers.
                    </div>
                  ) : (
                    <div className="text-sm text-grayscale-1100">
                      {row.horizon?.status === 'has' ? `Has Horizon data (${str(row.horizon?.horizon) || '—'}).` : 'No Horizon data.'}
                    </div>
                  )}
                </Section>
              )}

              <Section title="Traffic by day">
                <DailyBars days={byDay} series={[{ key: 'views', label: 'Views' }, { key: 'visitors', label: 'Visitors' }]} />
              </Section>

              <div className="grid md:grid-cols-2 gap-4">
                <Section title="Views by state">
                  {breakdown(d.by_state, k => nameOf(k, STATE_LABELS)).length
                    ? <BarList items={breakdown(d.by_state, k => nameOf(k, STATE_LABELS))} />
                    : <div className="text-sm text-grayscale-1100">No views in this period.</div>}
                </Section>
                <Section title="Came from">
                  {breakdown(d.referrers, k => nameOf(k, SOURCE_LABELS)).length
                    ? <BarList items={breakdown(d.referrers, k => nameOf(k, SOURCE_LABELS)).slice(0, 12)} />
                    : <div className="text-sm text-grayscale-1100">No referrers recorded.</div>}
                </Section>
              </div>

              <Section title="Per-section engagement">
                <p className="text-xs text-grayscale-1100 -mt-1">
                  Seen = the section scrolled into view (once per page load). Locked clicks = someone clicked the
                  blurred section's sign-in / upgrade button. Reach = seen ÷ page views.
                </p>
                <DataTable rows={sections} columns={SECTION_COLS} labels={SECTION_COL_LABELS} maxHeight="20rem"
                  empty="No section views in this period." />
              </Section>

              <div className="grid md:grid-cols-2 gap-4">
                <Section title="CTA clicks">
                  {breakdown(d.ctas, k => nameOf(k, CTA_LABELS)).length
                    ? <BarList items={breakdown(d.ctas, k => nameOf(k, CTA_LABELS))} />
                    : <div className="text-sm text-grayscale-1100">No CTA clicks in this period.</div>}
                </Section>
                <Section title="Shares by channel">
                  {share.length ? <BarList items={share} />
                    : <div className="text-sm text-grayscale-1100">Nobody shared this page in this period.</div>}
                </Section>
              </div>

              <Section title={`Recent events (${events.length})`}>
                <DataTable rows={events} columns={EVENT_COLS} labels={EVENT_LABELS} maxHeight="24rem"
                  empty="No events in this period."
                  render={{
                    at: r => <span className="text-xs" title={str(r.at)}>{fmtDate(r.at)}</span>,
                    action: r => (
                      <span className="flex flex-col leading-tight">
                        <span className="text-grayscale-1300">{str(r.label) || eventLabel(r.action)}</span>
                        <span className="text-[10px] font-mono text-grayscale-1100">{str(r.action)}</span>
                      </span>
                    ),
                    props: r => <span className="text-xs text-grayscale-1200" title={propsText(r.props)}>{propsText(r.props) || '—'}</span>,
                    state: r => r.state ? nameOf(r.state, STATE_LABELS) : '—',
                    session_id: r => <span className="font-mono text-[11px]">{str(r.session_id).slice(0, 12) || '—'}</span>,
                  }} />
              </Section>

              <Section title={`Sessions (${sessions.length})`}>
                <DataTable rows={sessions} columns={SESSION_COLS} labels={SESSION_LABELS} maxHeight="22rem"
                  empty="No sessions in this period."
                  render={{
                    at: r => <span className="text-xs" title={str(r.at)}>{fmtDate(r.at)}</span>,
                    user_email: r => r.user_email ? <span className="text-xs">{str(r.user_email)}</span>
                      : <span className="text-xs text-grayscale-1100">anonymous</span>,
                    country: r => [str(r.city), str(r.country)].filter(Boolean).join(', ') || '—',
                    referrer: r => <span className="text-xs block max-w-[16rem] truncate" title={str(r.referrer)}>{str(r.referrer) || 'direct'}</span>,
                    events: r => Array.isArray(r.events)
                      ? <span className="text-xs" title={r.events.map((e: any) => str(e?.action ?? e)).join(', ')}>{r.events.length}</span>
                      : fmtInt(r.events),
                    session_id: r => <span className="font-mono text-[11px]">{str(r.session_id).slice(0, 12) || '—'}</span>,
                  }} />
              </Section>

              {crawlerSeries.length > 0 && (
                <Section title="Crawler hits by day">
                  <DailyBars days={crawlerSeries} series={[{ key: 'hits', label: 'Crawler hits' }]} height="h-24" />
                </Section>
              )}
            </>
          )}

          <Section title="Preview as each state">
            <p className="text-xs text-grayscale-1100 -mt-1">
              Opens the public page with <span className="font-mono">preview_state</span>. The server honours it only
              for a signed-in admin, so this is exactly what that visitor would get — including which sections are locked.
            </p>
            {prev.error && <Msg kind="error">Preview links: {prev.error}</Msg>}
            <div className="flex gap-2 flex-wrap">
              {STATES.map(s => {
                const href = links[s] || withPreview(path, s);
                return href ? (
                  <a key={s} href={href} target="_blank" rel="noopener noreferrer" title={href}
                    className="py-1 px-2.5 text-xs rounded-full border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200 whitespace-nowrap">
                    {STATE_LABELS[s]} ↗
                  </a>
                ) : null;
              })}
            </div>
          </Section>

          <Section title="URL history">
            {history.length ? (
              <ul className="flex flex-col gap-1 text-xs">
                {history.map((h: any, i: number) => {
                  const p = typeof h === 'string' ? h : str(h?.path);
                  const canon = typeof h === 'object' ? !!h?.canonical : p === path;
                  return (
                    <li key={`${p}-${i}`} className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono break-all">{p}</span>
                      {canon ? <Pill tone="good">current</Pill> : <Pill tone="neutral" title="Answers 301 to the current URL">301 → current</Pill>}
                      {typeof h === 'object' && h?.created_at && (
                        <span className="text-grayscale-1100">issued {fmtDate(h.created_at)}</span>
                      )}
                      {typeof h === 'object' && h?.retired_at && (
                        <span className="text-grayscale-1100">· retired {fmtDate(h.retired_at)}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : <div className="text-xs text-grayscale-1100">Only the current URL — it has never changed.</div>}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default PageDrawer;
